import { getPhotoContent } from '../Photos/photoCache';
import { conditionMap, gradeMap } from '../../../common/mappings';

const inventoryAt = (src, key, index) => (index == null ? src?.[key] : src?.[key]?.[index]);

export const pickMapInventory = (src, index) => {
  const g = (key) => inventoryAt(src, key, index);
  const condition = g('Condition') ?? g('ConditionIn');
  const grade = g('Grade');
  return {
    style: g('Style') || '',
    sqft: g('Sqft') || 0,
    acres: g('Acres') || 0,
    RegridBldgFtPrnt: g('RegridBldgFtPrnt'),
    yearBuilt: g('YearBuilt'),
    effYearBuilt: g('EffYearBuilt'),
    fullBaths: g('Bath') ?? g('BathsFull') ?? g('fullBaths'),
    halfBaths: g('HalfBath') ?? g('BathsHalf') ?? g('halfBaths'),
    view: g('View') || (g('Waterfront') === 1 ? 'Waterfront' : g('Waterview') === 1 ? 'Waterview' : ''),
    condition: conditionMap[condition] || condition || '',
    grade: gradeMap[grade] || grade || '',
    pool: g('Pool') ?? g('InGroundPool'),
    basement: g('FinishedBasement'),
    garage: g('TotalGarageCap') ?? g('TotalGarageCapacity') ?? g('GarageCapacity'),
    fireplaces: g('Fireplaces'),
    deck: g('Deck'),
    patio: g('Patio'),
    porch: g('Porch'),
    kitchens: g('Kitchens'),
    bedrooms: g('Bedrooms'),
    cac: g('CentralAir'),
  };
};

const GRID_TO_MAP_INVENTORY = {
  Style: 'style', Sqft: 'sqft', Acres: 'acres', RegridBldgFtPrnt: 'RegridBldgFtPrnt',
  YearBuilt: 'yearBuilt', EffYearBuilt: 'effYearBuilt',
  Bath: 'fullBaths', BathsFull: 'fullBaths', fullBaths: 'fullBaths',
  HalfBath: 'halfBaths', BathsHalf: 'halfBaths', halfBaths: 'halfBaths',
  View: 'view', Condition: 'condition', ConditionIn: 'condition', Grade: 'grade',
  Pool: 'pool', InGroundPool: 'pool', FinishedBasement: 'basement',
  TotalGarageCap: 'garage', TotalGarageCapacity: 'garage', GarageCapacity: 'garage',
  Fireplaces: 'fireplaces', Deck: 'deck', Patio: 'patio', Porch: 'porch',
  Kitchens: 'kitchens', Bedrooms: 'bedrooms', CentralAir: 'cac',
  Latitude: 'latitude', Longitude: 'longitude',
};

export const overlayMapInventory = (base, overlay) => {
  if (!base || !overlay) return base;
  const next = { ...base };
  for (const [from, to] of Object.entries(GRID_TO_MAP_INVENTORY)) {
    if (overlay[from] === undefined) continue;
    let value = overlay[from];
    if (from === 'Condition' || from === 'ConditionIn') value = conditionMap[value] || value;
    if (from === 'Grade') value = gradeMap[value] || value;
    next[to] = value;
  }
  return next;
};

export const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

export const formatPrice = (price) => {
  if (!price) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);
};

export const formatPercentage = (value) => {
  if (!value) return 'N/A';
  return `${value.toFixed(1)}%`;
};

export const formatAbbreviatedPrice = (price) => {
  if (!price) return 'N/A';
  const num = Number(price);
  if (num >= 1000000) {
    return `$${(num / 1000000).toFixed(2)}m`;
  } else if (num >= 1000) {
    return `$${(num / 1000).toFixed(0)}k`;
  }
  return `$${num.toLocaleString()}`;
};

export const buildSingleTooltipHTML = (feature, isDarkMode, subjectIFMV, reportType) => {
  if (!feature) return '';
  const properties = feature.properties || feature;
  
  // Determine price color based on comparison with subject IFMV
  let priceColor = '#DC2626'; // Default to red
  if (properties.isSubject) {
    priceColor = '#FFD700'; // Gold for subject
  } else if (properties.adjustedPrice && subjectIFMV) {
    // Green if below IFMV, red if above
    priceColor = Number(properties.adjustedPrice) < Number(subjectIFMV) ? '#16A34A' : '#DC2626';
  }

  // Format reduction %
  let reductionDisplay = '';
  if (typeof properties.reductionPercentage === 'number') {
    const val = properties.reductionPercentage;
    reductionDisplay = `<span style=\"margin-left:6px; color:${val < 0 ? '#16A34A' : '#DC2626'}; font-weight:bold;\">${val > 0 ? '+' : ''}${val.toFixed(1)}%</span>`;
  }

  // Status line with color for Selected #
  let statusLine = '';
  if (properties.isSubject) {
    statusLine = `<span style="font-weight: bold;">IFMV:</span> <span style="font-weight: bold;">${formatAbbreviatedPrice(subjectIFMV)}</span>`;
  } else if (properties.selected) {
    statusLine = `<span style=\"color:#4169E1; font-weight:500;\">Selected #${properties.selectedRowNumber}</span>`;
  } else {
    statusLine = `Algorithm #${properties.id_number || ''}`;
  }

  // Determine which value to show for comps
  let valueToShow;
  if (properties.isSubject) {
    valueToShow = formatAbbreviatedPrice(subjectIFMV);
  } else if (reportType === 'assessment') {
    valueToShow = formatAbbreviatedPrice(properties.adjustedIFMV);
  } else {
    valueToShow = formatAbbreviatedPrice(properties.adjustedPrice);
  }

  return `
    <div style=\"font-size:14px; line-height:1.4; min-width: 250px;\">
      <div style=\"background: #f8f9fa; height: 120px; display: flex; align-items: center; justify-content: center; color: #666;\">
        ${typeof getPhotoContent === 'function' ? getPhotoContent(properties.parcel_id) : ''}
      </div>
      <div style=\"padding: 8px;\">
        <div style=\"display: flex; flex-direction: row; justify-content: space-between; align-items: flex-start; gap: 8px;\">
          <div style=\"display: flex; flex-direction: column; align-items: flex-start; min-width: 0;\">
            <div style=\"display: flex; align-items: center; min-width: 0;\">
              <span style=\"font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;\">${properties.address || 'No address'}</span>
              ${reductionDisplay}
            </div>
            ${properties.ALFlag === 0 ? '<div style=\"font-size: 12px; color: #DC2626; font-weight: 500; margin-top: 2px;\">NAL Sale</div>' : ''}
            <div style=\"font-size: 12px; color: #666; display: flex; align-items: center; gap: 4px;\">
              ${statusLine}
              <span class=\"custom-tooltip-checkbox\" id=\"mui-checkbox-tooltip\"></span>
            </div>
          </div>
          <div style=\"display: flex; flex-direction: column; align-items: flex-end; min-width: 0;\">
            <span style=\"color: ${priceColor}; font-weight: bold; white-space: nowrap;\">${valueToShow}</span>
            <span style=\"font-size: 11px; color: #666;\">${formatDate(properties.saleDate)}</span>
          </div>
        </div>
        <div style=\"font-size: 14px; color: #888; display: flex; align-items: center; gap: 8px; margin-top: 2px;\">
          ${[
            properties.style ? `<span>${properties.style}</span>` : '',
            properties.sqft ? (() => {
              const sqft = Number(properties.sqft);
              const [integerPart, decimalPart] = sqft.toString().split('.');
              if (decimalPart && decimalPart.length >= 2) {
                return `<span style="color: red">${Math.round(sqft).toLocaleString()}* sqft</span>`;
              }
              return `<span>${sqft.toLocaleString()} sqft</span>`;
            })() : '',
            properties.acres ? `${Number(properties.acres).toFixed(2)} acres` : ''
          ].filter(Boolean).join('<span style=\"color:#d1d5db;\"> | </span>')}
        </div>
      </div>
    </div>
  `;
};

export const buildMultiTooltipHTML = (features, isDarkMode, reportType) => {
  return `
    <div style="font-size:14px; line-height:1.4; max-width: 600px;">
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
        <div style="width: 32px; height: 32px; background: #4169E1; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; color: white;">
          ${features.length}
        </div>
        <div style="font-size: 16px; font-weight: bold; color: #4169E1;">Multiple Properties</div>
      </div>
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
        ${features.map(f => {
          const comp = f.properties || f;
          const color = comp.isSubject ? '#FFD700' : comp.selected ? '#4169E1' : comp.supportsReduction ? '#16A34A' : '#DC2626';
          let valueToShow;
          if (comp.isSubject) {
            valueToShow = formatAbbreviatedPrice(comp.ifmv);
          } else if (reportType === 'assessment') {
            valueToShow = formatAbbreviatedPrice(comp.adjustedIFMV);
          } else {
            valueToShow = formatAbbreviatedPrice(comp.adjustedPrice);
          }
          return `
            <div data-parcel-id="${comp.parcel_id}" style="background: ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#f8f9fa'}; padding: 8px; border-radius: 4px; border-left: 3px solid ${color}; display: flex; flex-direction: column; gap: 2px; cursor: pointer;">
              <div style="display: flex; align-items: center; gap: 6px;">
                <span style="font-weight: bold; color: ${comp.isSubject ? '#FFD700' : comp.selected ? '#4169E1' : color};">${comp.isSubject ? 'Subject' : comp.selected ? `Comp ${comp.selectedRowNumber || ''}` : comp.supportsReduction ? 'Good' : 'Bad'}</span>
                <span style="font-size: 12px; color: ${isDarkMode ? 'rgba(255,255,255,0.8)' : '#666'};">${comp.address || 'No address'}</span>
              </div>
              <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 2px;">
                <div style="font-size: 12px; color: ${comp.supportsReduction ? '#16A34A' : '#DC2626'};">${valueToShow}</div>
                <span class="custom-tooltip-checkbox" id="multi-checkbox-TOOLTIP_${comp.parcel_id}"></span>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    </div>`;
}; 